import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const VIEWERS = new Set(["back_office", "super_admin"]);

// Amber strip on a Lead or a PO when the auto-matcher found the same customer
// (e-mail / phone) on the other side. VIN matches merge by themselves; this one
// needs a human: Merge → approve-lead (lead absorbed into the PO → Advance).
export function MatchSuggestionBanner({ order, user }) {
  const { t } = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const list = Array.isArray(order?.match_suggestions) ? order.match_suggestions : [];
  const merge = useMutation({
    mutationFn: (s) => {
      const leadId = order.is_lead ? order.id : s.id;
      const po = order.is_lead ? s.po_number : order.po_number;
      return api.post(`/orders/${leadId}/approve-lead`, { po_number: po, payment_confirmed: true }).then((r) => r.data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries();
      toast.success(`🔗 ${t("Merged")} → ${data?.po_number || ""}`);
      if (data?.id && data.id !== order.id) navigate(`/order/${data.id}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || t("Merge failed")),
  });
  const dismiss = useMutation({
    mutationFn: (s) => api.post(`/orders/${order.id}/dismiss-match`, { other_id: s.id }),
    onSuccess: () => { qc.invalidateQueries(); toast.success(t("Suggestion hidden")); },
  });
  if (!VIEWERS.has(user?.role) || list.length === 0) return null;
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm space-y-2" data-testid="match-suggestion-banner">
      <div className="font-bold text-amber-900">
        🔗 {order.is_lead ? t("This lead may belong to an existing order") : t("A lead may belong to this order")}
        <span className="font-normal text-amber-800"> · {t("VIN matches merge automatically — this one matched on contact details, please confirm")}</span>
      </div>
      {list.map((s) => (
        <div key={s.id} className="flex flex-wrap items-center gap-2" data-testid={`match-suggestion-${s.id}`}>
          <Link to={`/order/${s.id}`} className="font-mono font-bold underline text-amber-900">{s.po_number}</Link>
          <span className="text-xs text-amber-800">({t(s.reason)})</span>
          <Button size="sm" className="ml-auto h-7 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={merge.isPending}
            onClick={() => merge.mutate(s)} data-testid={`match-merge-${s.id}`}>
            {merge.isPending ? t("Merging…") : `🔗 ${order.is_lead ? `${t("Merge into")} ${s.po_number}` : t("Merge lead into this order")}`}
          </Button>
          <Button size="sm" variant="outline" className="h-7 border-amber-400 text-amber-900" disabled={dismiss.isPending}
            onClick={() => dismiss.mutate(s)} data-testid={`match-dismiss-${s.id}`}>
            ✕ {t("Not the same car")}
          </Button>
        </div>
      ))}
    </div>
  );
}
